import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Plus } from '@untitledui/icons/Plus';
import { X } from '@untitledui/icons/X';
import { useState, type FormEvent } from 'react';

import type { CreditCardDto } from '@/api/core-api/types.gen';

import {
  creditCardsControllerRemove,
  creditCardsControllerUpdate,
  creditCardsControllerUpdateDefaultCard,
} from '@/api/core-api';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { Card } from '@/components/base/card/card';
import { ConfirmationModal } from '@/components/base/modals/confirmation-modal';
import { queryKeys } from '@/hooks/api/query-keys';
import { useCreditCards } from '@/hooks/api/use-credit-cards';
import { CreditCardFormSection } from '@/routes/researchers/_authenticated/projects/-components/credit-card-form-section';
import { getAxiosErrorMessage, getAxiosErrorStatus } from '@/utils/error';

import { cardMutationErrorMessage } from './-card-error-message';

export const Route = createFileRoute(
  '/researchers/_authenticated/billing/credit-cards',
)({
  component: BillingCardsPage,
  head: () => ({ meta: [{ title: t`Credit cards` }] }),
});

function BillingCardsPage() {
  const { user, activeDashboard } = Route.useRouteContext();
  const teamId = activeDashboard.team ?? '';
  const client = useQueryClient();
  const {
    data: cards,
    isPending,
    isError,
  } = useCreditCards(teamId, { enabled: !!teamId });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [removing, setRemoving] = useState<CreditCardDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = () =>
    void client.invalidateQueries({
      queryKey: queryKeys.creditCards.byTeamId(teamId),
    });
  const mutation = useMutation({
    mutationFn: async (action: {
      kind: 'default' | 'remove' | 'edit';
      token: string;
      expirationDate?: string;
      streetAddress?: string;
      postalCode?: string;
      cvv?: string;
    }) => {
      switch (action.kind) {
        case 'default':
          await creditCardsControllerUpdateDefaultCard({
            path: { teamId },
            body: { token: action.token },
            throwOnError: true,
          });
          break;
        case 'remove':
          await creditCardsControllerRemove({
            path: { teamId, token: action.token },
            throwOnError: true,
          });
          break;
        case 'edit':
          await creditCardsControllerUpdate({
            path: { teamId },
            body: {
              token: action.token,
              expirationDate: action.expirationDate,
              cvv: action.cvv,
              billingAddress: {
                streetAddress: action.streetAddress,
                postalCode: action.postalCode,
              },
            },
            throwOnError: true,
          });
          break;
      }
    },
    onSuccess: () => {
      setError(null);
      setEditing(null);
      setRemoving(null);
      refresh();
    },
    onError: (mutationError) =>
      setError(
        cardMutationErrorMessage(
          getAxiosErrorStatus(mutationError),
          getAxiosErrorMessage(mutationError),
        ),
      ),
  });
  const run = (
    kind: 'default' | 'remove' | 'edit',
    token: string,
    details?: {
      expirationDate?: string;
      streetAddress?: string;
      postalCode?: string;
      cvv?: string;
    },
  ) => mutation.mutate({ kind, token, ...details });
  const removingLabel = `${removing?.cardType ?? t`Card`} •••• ${removing?.last4 ?? '••••'}`;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-display-xs font-semibold text-primary">
          <Trans>Credit cards</Trans>
        </h1>
        <Button
          size="sm"
          color="secondary"
          iconLeading={adding ? X : Plus}
          isDisabled={!user.isEmailVerified}
          onClick={() => setAdding(!adding)}
        >
          {adding ? t`Cancel` : t`Add credit card`}
        </Button>
      </div>
      {!user.isEmailVerified && (
        <p className="text-sm text-secondary">
          <Trans>
            Verify your email address before adding a payment method.
          </Trans>
        </p>
      )}
      {adding && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CreditCardFormSection
              teamId={teamId}
              projectId=""
              onCardSaved={() => {
                setAdding(false);
                refresh();
              }}
            />
          </Card>
        </div>
      )}
      {isPending && (
        <p role="status" className="text-sm text-secondary">
          <Trans>Loading payment methods…</Trans>
        </p>
      )}
      {isError && (
        <p role="alert" className="text-sm text-error-primary">
          <Trans>Could not load credit cards.</Trans>
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-error-primary">
          {error}
        </p>
      )}
      {cards?.length === 0 && !adding && (
        <p className="text-sm text-secondary">
          <Trans>No credit cards on file.</Trans>
        </p>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        {cards?.map((card) => (
          <Card
            key={card.token ?? `${card.last4}-${card.expirationDate}`}
            className="space-y-3"
          >
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-semibold text-primary">
                {card.cardType ?? t`Card`} •••• {card.last4 ?? '••••'}
              </h2>
              {card.default && (
                <Badge size="sm" color="success">
                  <Trans>Default</Trans>
                </Badge>
              )}
            </div>
            <p className="text-sm text-secondary">
              <Trans>Expires</Trans>{' '}
              {card.expirationDate ??
                `${card.expirationMonth ?? '—'}/${card.expirationYear ?? '—'}`}
              {card.expired && ` · ${t`Expired`}`}
            </p>
            {card.token && (
              <div className="flex flex-wrap gap-2">
                {!card.default && (
                  <Button
                    size="sm"
                    color="secondary"
                    isDisabled={mutation.isPending}
                    onClick={() => run('default', card.token ?? '')}
                  >
                    <Trans>Make default</Trans>
                  </Button>
                )}
                <Button
                  size="sm"
                  color="secondary"
                  isDisabled={mutation.isPending}
                  onClick={() => {
                    setEditing(
                      editing === card.token ? null : (card.token ?? null),
                    );
                    setRemoving(null);
                  }}
                >
                  <Trans>Edit</Trans>
                </Button>
                <Button
                  size="sm"
                  color="tertiary-destructive"
                  isDisabled={mutation.isPending}
                  onClick={() => {
                    setRemoving(card);
                    setError(null);
                    setEditing(null);
                  }}
                >
                  <Trans>Remove</Trans>
                </Button>
              </div>
            )}
            {card.token && editing === card.token && (
              <EditCard
                card={card}
                busy={mutation.isPending}
                onCancel={() => setEditing(null)}
                onSave={(details) => run('edit', card.token ?? '', details)}
              />
            )}
          </Card>
        ))}
      </div>
      <ConfirmationModal
        isOpen={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={t`Remove ${removingLabel}?`}
        description={t`This card will be permanently removed from your team.`}
        confirmLabel={t`Remove card`}
        isDestructive
        isLoading={mutation.isPending}
        onConfirm={() => removing?.token && run('remove', removing.token)}
        onCancel={() => setRemoving(null)}
      >
        {error && (
          <p role="alert" className="mt-3 text-sm text-error-primary">
            {error}
          </p>
        )}
      </ConfirmationModal>
    </>
  );
}

function EditCard({
  card,
  busy,
  onSave,
  onCancel,
}: {
  card: CreditCardDto;
  busy: boolean;
  onSave: (details: {
    expirationDate: string;
    streetAddress: string;
    postalCode: string;
    cvv: string;
  }) => void;
  onCancel: () => void;
}) {
  const [expirationDate, setExpirationDate] = useState(
    card.expirationDate ?? '',
  );
  const [streetAddress, setStreetAddress] = useState(
    card.billingAddress?.streetAddress ?? card.streetAddress ?? '',
  );
  const [postalCode, setPostalCode] = useState(
    card.billingAddress?.postalCode ?? card.postalCode ?? '',
  );
  const [cvv, setCvv] = useState('');
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave({ expirationDate, streetAddress, postalCode, cvv });
  };
  return (
    <form
      onSubmit={submit}
      className="space-y-3 border-t border-secondary pt-3 text-sm text-secondary"
    >
      <label className="flex flex-col gap-1">
        <Trans>Expiration (MM/YYYY)</Trans>
        <input
          required
          pattern="(0[1-9]|1[0-2])/20[0-9]{2}"
          value={expirationDate}
          onChange={(event) => setExpirationDate(event.target.value)}
          className="rounded-lg border border-secondary p-2"
        />
      </label>
      <label className="flex flex-col gap-1">
        <Trans>Billing address</Trans>
        <input
          required
          value={streetAddress}
          onChange={(event) => setStreetAddress(event.target.value)}
          className="rounded-lg border border-secondary p-2"
        />
      </label>
      <label className="flex flex-col gap-1">
        <Trans>Zip code</Trans>
        <input
          required
          value={postalCode}
          onChange={(event) => setPostalCode(event.target.value)}
          className="rounded-lg border border-secondary p-2"
        />
      </label>
      <label className="flex flex-col gap-1">
        <Trans>Security code (CVV)</Trans>
        <input
          required
          inputMode="numeric"
          autoComplete="off"
          pattern="[0-9]{3,4}"
          maxLength={4}
          value={cvv}
          onChange={(event) => setCvv(event.target.value)}
          className="w-28 rounded-lg border border-secondary p-2"
        />
      </label>
      <div className="flex gap-2">
        <Button type="submit" size="sm" isDisabled={busy}>
          <Trans>Save</Trans>
        </Button>
        <Button type="button" color="secondary" size="sm" onClick={onCancel}>
          <Trans>Cancel</Trans>
        </Button>
      </div>
    </form>
  );
}
